import { useEffect, useState } from "react";
import { Loader2, Filter, TrendingDown, FlaskConical } from "lucide-react";
import api from "../lib/apiClient";

const RANGES = [["all", "Tout"], ["30d", "30 jours"], ["7d", "7 jours"]];
const AB_LABELS = {
  landing_view: "Vue landing", landing_cta_click: "Clic CTA", diagnostic_started: "Diagnostic démarré",
  diagnostic_completed: "Diagnostic terminé", lead_created: "Lead", signup_completed: "Inscription",
  activation_completed: "Activation",
};

export default function AdminFunnel() {
  const [range, setRange] = useState("all");
  const [data, setData] = useState(null);
  const [ab, setAb] = useState(null);

  useEffect(() => { api.get(`/admin/funnel?range=${range}`).then((r) => setData(r.data)).catch(() => setData(false)); }, [range]);
  useEffect(() => { api.get("/admin/funnel/ab").then((r) => setAb(r.data)).catch(() => {}); }, []);

  if (data === false) return <p className="text-red-400">Accès réservé.</p>;
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  const maxCount = Math.max(...data.steps.map((s) => s.count), 1);

  return (
    <div className="space-y-8" data-testid="admin-funnel-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><Filter className="w-7 h-7 text-amber-400" /> Sales Funnel</h1>
          <p className="text-zinc-500 text-sm mt-1">Tunnel d'acquisition & de conversion — données réelles uniquement.</p>
        </div>
        <div className="flex gap-1 bg-[#0F0F13] border border-zinc-800 rounded-xl p-1">
          {RANGES.map(([k, l]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`funnel-range-${k}`}
              className={`px-3 h-9 rounded-lg text-sm ${range === k ? "bg-amber-500/15 text-amber-400" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Funnel steps */}
      <div className="space-y-2" data-testid="funnel-steps">
        {data.steps.map((s, i) => (
          <div key={s.key} className="bg-[#18181E] border border-zinc-800 rounded-xl p-4" data-testid={`funnel-step-${s.key}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-zinc-200">{i + 1}. {s.label}</span>
              <span className="font-display text-xl font-extrabold text-amber-400" data-testid={`funnel-count-${s.key}`}>{s.count}</span>
            </div>
            <div className="h-2 bg-zinc-900 rounded-full overflow-hidden">
              <div className="h-full gold-gradient" style={{ width: `${Math.max((s.count / maxCount) * 100, 2)}%` }} />
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs">
              <span className="text-emerald-400">Conv. étape préc. : <b>{s.conv_from_prev}%</b></span>
              {i > 0 && <span className="text-red-400 inline-flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Abandon : <b>{s.drop_rate}%</b></span>}
            </div>
          </div>
        ))}
      </div>

      {/* Conversions par période */}
      <div>
        <h2 className="font-display text-lg font-bold mb-3">Conversions par période</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["leads", "signups", "activated", "paid"].map((k) => (
            <div key={k} className="bg-[#18181E] border border-zinc-800 rounded-xl p-4" data-testid={`period-${k}`}>
              <p className="text-xs text-zinc-500 uppercase tracking-wide">{k}</p>
              <p className="text-sm text-zinc-300 mt-2">7j : <b className="text-amber-400">{data.periods["7d"][k]}</b></p>
              <p className="text-sm text-zinc-300">30j : <b className="text-amber-400">{data.periods["30d"][k]}</b></p>
            </div>
          ))}
        </div>
      </div>

      {/* Attribution */}
      <div className="grid md:grid-cols-2 gap-4">
        <AttrCard title="First-touch" attr={data.attribution.first_touch} testid="attr-first" />
        <AttrCard title="Last-touch" attr={data.attribution.last_touch} testid="attr-last" />
      </div>

      {/* A/B testing */}
      {ab && (
        <div data-testid="ab-results">
          <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2"><FlaskConical className="w-5 h-5 text-amber-400" /> A/B Hero & CTA</h2>
          <p className="text-xs text-zinc-500 mb-3">{ab.note}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-zinc-800 rounded-xl overflow-hidden">
              <thead className="bg-[#0F0F13] text-zinc-400">
                <tr><th className="text-left p-3">Étape</th><th className="p-3">Variante A</th><th className="p-3">Variante B</th></tr>
              </thead>
              <tbody>
                {ab.steps.map((st) => (
                  <tr key={st} className="border-t border-zinc-800" data-testid={`ab-row-${st}`}>
                    <td className="p-3 text-zinc-300">{AB_LABELS[st] || st}</td>
                    <td className="p-3 text-center text-amber-400 font-semibold">{ab.variants.A?.[st] ?? 0}</td>
                    <td className="p-3 text-center text-amber-300 font-semibold">{ab.variants.B?.[st] ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AttrCard({ title, attr, testid }) {
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-xl p-4" data-testid={testid}>
      <h3 className="font-semibold text-zinc-200 mb-3">{title}</h3>
      {["source", "medium", "campaign"].map((dim) => (
        <div key={dim} className="mb-3">
          <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">{dim}</p>
          {Object.keys(attr[dim] || {}).length === 0 ? (
            <p className="text-xs text-zinc-600">Aucune donnée</p>
          ) : (
            Object.entries(attr[dim]).slice(0, 6).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm text-zinc-300"><span>{k}</span><b className="text-amber-400">{v}</b></div>
            ))
          )}
        </div>
      ))}
    </div>
  );
}
